import {
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';
import { ApprovalRule, ApprovalRuleLevel } from './types';

export interface RuleRowProps {
  rule: ApprovalRule;
  onEdit?: () => void;
  onToggle?: (enabled: boolean) => void;
  onDelete?: () => void;
  /** True while this row's own toggle request is in flight. */
  toggling?: boolean;
}

/** Named approvers plus role approvers — both count toward a level's coverage. */
const approverCount = (level: ApprovalRuleLevel) => level.approvers?.length ?? 0;

/**
 * One rule in the list.
 *
 * A single-level rule renders its approvers inline: the level adds no
 * information the parent row doesn't already carry, so nesting it would be a
 * disclosure step that reveals nothing. A multi-level rule nests, because the
 * order of levels is the rule's actual behaviour — level 2 does not open until
 * level 1 is satisfied — and a flat summary cannot express that.
 */
export const RuleRow: React.FC<RuleRowProps> = ({
  rule,
  onEdit,
  onToggle,
  onDelete,
  toggling = false,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const levels = rule.levels ?? [];
  const isMultiLevel = levels.length > 1;
  // Not a permission — the account's admins may edit rules, this particular rule
  // is owned by the legacy backfill and would have its changes overwritten. An
  // action that is unavailable because of what the *record* is, rather than who
  // the user is, is not shown at all; the badge and hint already say why.
  const isEditable = !rule.managed_by_legacy_group;

  return (
    <div
      className={classNames(styles.rule, { [styles.ruleDisabled]: !rule.enabled })}
      data-testid="approval-rule"
    >
      <div className={styles.ruleHeader}>
        <Typography variant="body2" className={styles.ruleName}>
          {rule.name}
        </Typography>

        {/* Text, not colour alone — an "Off" rule has to read as off in a
            screenshot, a screen reader, and at 50% opacity. */}
        {!rule.enabled && (
          <Chip size="small" label={intl.formatMessage(messages.disabledRule)} />
        )}

        {rule.managed_by_legacy_group && (
          <Chip
            size="small"
            label={intl.formatMessage(messages.managedElsewhere)}
            data-testid="managed-elsewhere"
          />
        )}

        {isEditable && (
          <div className={styles.rowActions}>
            {onToggle && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={Boolean(rule.enabled)}
                    disabled={toggling}
                    onChange={event => onToggle(event.target.checked)}
                    inputProps={{
                      'aria-label': `${intl.formatMessage(messages.ruleActiveToggle)} — ${rule.name}`,
                    }}
                  />
                }
                label={intl.formatMessage(messages.ruleActiveToggle)}
              />
            )}

            {onEdit && (
              <Button size="small" onClick={onEdit}>
                {intl.formatMessage(globalMessages.edit)}
              </Button>
            )}

            {/* Last, and never a filled button: destructive actions stay
                tertiary weight so the row's primary affordance is editing. */}
            {onDelete && (
              <Button
                size="small"
                className={styles.dangerButton}
                onClick={onDelete}
                aria-label={`${intl.formatMessage(messages.deleteRule)} — ${rule.name}`}
              >
                {intl.formatMessage(messages.deleteRule)}
              </Button>
            )}
          </div>
        )}
      </div>

      <Typography variant="subtitle1" color="textSecondary" className={styles.summary}>
        <FormattedMessage
          {...messages.levelCount}
          values={{ count: rule.level_count ?? levels.length }}
        />
      </Typography>

      {/* The badge says a rule is managed elsewhere; this says what to do about
          it. A badge on its own leaves the admin to guess why the row has no
          actions — and the consequence of guessing wrong (editing it on the old
          page vs. giving up) is the whole point of surfacing it. */}
      {rule.managed_by_legacy_group && (
        <Typography variant="subtitle1" color="textSecondary" className={styles.managedHint}>
          <FormattedMessage {...messages.managedElsewhereHint} />
        </Typography>
      )}

      {isMultiLevel && (
        <div className={styles.levels}>
          {levels.map((level, index) => (
            <div
              // Levels have no client-visible id and are ordered, so position is
              // the identity here — `position` when the server sent it, falling
              // back to array index for a rule that has not been saved yet.
              key={level.position ?? index}
              className={classNames({ [styles.levelDisabled]: !level.enabled })}
              data-testid="approval-rule-level"
            >
              <Typography variant="subtitle1">
                {level.name}
                {' — '}
                <FormattedMessage
                  {...messages.approverCount}
                  values={{ count: approverCount(level) }}
                />
              </Typography>
            </div>
          ))}
        </div>
      )}

      {!isMultiLevel && levels.length === 1 && (
        <Typography variant="subtitle1" color="textSecondary" data-testid="inline-approvers">
          <FormattedMessage
            {...messages.approverCount}
            values={{ count: approverCount(levels[0]) }}
          />
        </Typography>
      )}
    </div>
  );
};
